import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export type TurnoverFlowPhase =
  | "entry"
  | "cleaning"
  | "inventory_guest"
  | "inventory_supply"
  | "inventory_single"
  | "complete";

interface TurnoverStepStripProps {
  phase: TurnoverFlowPhase;
  showEntryStep: boolean;
  splitInventory: boolean;
  tDynamic: (s: string) => string;
  className?: string;
}

function StepDot({
  state,
  label,
  sublabel,
}: {
  state: "done" | "current" | "upcoming";
  label: string;
  sublabel?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-1 min-w-0 flex-1 text-center">
      <div
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-xs font-semibold",
          state === "done" && "border-primary bg-primary text-primary-foreground",
          state === "current" && "border-primary bg-background text-primary ring-2 ring-primary/30",
          state === "upcoming" && "border-muted-foreground/30 bg-muted/50 text-muted-foreground"
        )}
      >
        {state === "done" ? <Check className="h-4 w-4" /> : null}
      </div>
      <div className="min-w-0 px-0.5">
        <p
          className={cn(
            "text-[10px] sm:text-xs font-medium leading-tight break-words",
            state === "current" ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {label}
        </p>
        {sublabel ? (
          <p className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight mt-0.5">{sublabel}</p>
        ) : null}
      </div>
    </div>
  );
}

function Connector({ done }: { done: boolean }) {
  return (
    <div
      className={cn("h-0.5 w-4 sm:w-6 shrink-0 self-start mt-4 rounded-full", done ? "bg-primary" : "bg-muted-foreground/20")}
      aria-hidden
    />
  );
}

/** Read-only turnover progress (entry → clean → inventory). Not interactive. */
export function TurnoverStepStrip({ phase, showEntryStep, splitInventory, tDynamic, className }: TurnoverStepStripProps) {
  const entryDone = phase !== "entry";
  const cleanDone =
    phase === "inventory_guest" || phase === "inventory_supply" || phase === "inventory_single" || phase === "complete";

  const entryState: "done" | "current" | "upcoming" = phase === "entry" ? "current" : entryDone ? "done" : "upcoming";
  const cleanState: "done" | "current" | "upcoming" =
    phase === "cleaning" ? "current" : phase === "entry" ? "upcoming" : "done";

  let invLabel = tDynamic("Inventory");
  let invSub: string | undefined;
  let invState: "done" | "current" | "upcoming" = "upcoming";

  if (splitInventory) {
    if (phase === "inventory_guest") {
      invState = "current";
      invLabel = tDynamic("Guest amenities");
      invSub = tDynamic("After cleaning");
    } else if (phase === "inventory_supply") {
      invState = "current";
      invLabel = tDynamic("Supply closet");
      invSub = tDynamic("Last check");
    } else if (phase === "complete") {
      invState = "done";
      invLabel = tDynamic("Inventory");
      invSub = tDynamic("Done");
    } else if (phase === "cleaning") {
      invState = "upcoming";
      invLabel = tDynamic("Inventory");
      invSub = tDynamic("Guest then supply");
    }
  } else {
    if (phase === "inventory_single") {
      invState = "current";
      invLabel = tDynamic("Inventory");
      invSub = tDynamic("After cleaning");
    } else if (phase === "complete") {
      invState = "done";
      invLabel = tDynamic("Inventory");
    } else if (phase === "cleaning") {
      invState = "upcoming";
      invLabel = tDynamic("Inventory");
    }
  }

  const parts: ReactNode[] = [];
  if (showEntryStep) {
    parts.push(<StepDot key="e" state={entryState} label={tDynamic("Entry")} sublabel={tDynamic("Arrival")} />);
    parts.push(<Connector key="ec" done={entryDone} />);
  }
  parts.push(<StepDot key="cl" state={cleanState} label={tDynamic("Clean")} sublabel={tDynamic("Checklist")} />);
  parts.push(<Connector key="ci" done={cleanDone} />);
  parts.push(<StepDot key="in" state={invState} label={invLabel} sublabel={invSub} />);

  return (
    <div className={cn("rounded-lg border bg-muted/30 px-2 py-3 sm:px-3", className)} role="status" aria-live="polite">
      <div className="flex items-start justify-between gap-0.5 sm:gap-1">{parts}</div>
    </div>
  );
}

export function getTurnoverFlowSummary(
  phase: TurnoverFlowPhase,
  _showEntryStep: boolean,
  splitInventory: boolean,
  tDynamic: (s: string) => string
): string {
  if (phase === "entry") return tDynamic("Entry walkthrough");
  if (phase === "cleaning") return tDynamic("Cleaning checklist");
  if (phase === "inventory_guest") return tDynamic("Inventory: guest amenities");
  if (phase === "inventory_supply") return tDynamic("Inventory: supply closet");
  if (phase === "inventory_single") return splitInventory ? tDynamic("Inventory") : tDynamic("Inventory check");
  return tDynamic("Complete");
}

export function resolveTurnoverPhase(args: {
  showEntryWalkthrough: boolean;
  cleaningFlowState: "checklist" | "inventory" | "complete";
  inventoryGuestPhaseCompleted: boolean | null | undefined;
  hasSplitPostCleanInventory: boolean;
}): TurnoverFlowPhase {
  void args.inventoryGuestPhaseCompleted;
  void args.hasSplitPostCleanInventory;
  const { showEntryWalkthrough, cleaningFlowState } = args;
  if (showEntryWalkthrough) return "entry";
  if (cleaningFlowState === "complete") return "complete";
  if (cleaningFlowState === "checklist") return "cleaning";
  return "inventory_single";
}
